import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Switch,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  CancelOutlined,
  CheckCircle,
  Close,
  East,
  EmojiEvents,
} from "@mui/icons-material";
import { CompareContext } from "../../context/compare.context";
import { TripFiltersContext } from "../../context/trip-filters.context";
import { getPartnerLogo } from "../../service/logo.service";
import {
  formatBRL,
  getTotalPrice,
  isStudentOnly,
} from "../../utils/coverage.utils";
import {
  buildComparison,
  coverageCounts,
  planBadges,
  planSchengen,
} from "../../utils/compare.utils";
import {
  CoverageDialog,
  useSelectInsurance,
  useTripDays,
} from "../../components/insurance-offer/insurance-offer.component";

const BADGE_COLORS = {
  "Melhor preço": "success",
  "Maior cobertura médica": "primary",
  "Melhor custo-benefício": "warning",
};

// Critério de cada selo (aparece no resumo e ao passar o mouse no selo).
const BADGE_HINTS = {
  "Melhor preço": "Menor preço total da viagem.",
  "Maior cobertura médica": "Maior valor para despesas médicas e hospitalares.",
  "Melhor custo-benefício": "Mais cobertura médica por real pago.",
};

// Uma célula da tabela (valor de uma cobertura para um plano).
const Cell = ({ cell, winner }) => {
  const theme = useTheme();

  let content;
  if (cell.status === "included") {
    content = (
      <Stack direction="row" spacing={0.75} alignItems="flex-start">
        <CheckCircle sx={{ color: "success.main", fontSize: 18, mt: "1px" }} />
        <Box>
          <span>{cell.display}</span>
          {cell.note && (
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "text.secondary",
                lineHeight: 1.2,
              }}
            >
              {cell.note}
            </Typography>
          )}
        </Box>
      </Stack>
    );
  } else if (cell.status === "no") {
    content = (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ color: "text.disabled" }}
      >
        <CancelOutlined sx={{ fontSize: 18 }} />
        <span>{cell.display}</span>
      </Stack>
    );
  } else if (cell.status === "unknown") {
    content = (
      <Tooltip title="A seguradora não informou este item na cotação.">
        <Box component="span" sx={{ color: "text.disabled" }}>
          —
        </Box>
      </Tooltip>
    );
  } else {
    content = <span>{cell.display}</span>;
  }

  return (
    <Box
      sx={{
        px: { xs: 1, md: 2 },
        py: 1.25,
        fontSize: { xs: "0.85rem", md: "0.95rem" },
        fontWeight: winner ? 800 : 500,
        color: "text.primary",
        backgroundColor: winner
          ? alpha(theme.palette.success.main, 0.1)
          : "transparent",
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 0.25,
        minWidth: 0,
        overflowWrap: "anywhere",
      }}
    >
      {content}
      {winner && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ color: "success.dark" }}
        >
          <EmojiEvents sx={{ fontSize: 14 }} />
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>
            Maior valor
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

// Cabeçalho de cada coluna: seguradora, preço, selos e botão de compra.
const PlanHeader = ({
  plan,
  price,
  perDay,
  badges,
  count,
  isEurope,
  onRemove,
  onSelect,
}) => {
  const [coverageOpen, setCoverageOpen] = useState(false);

  return (
    <Stack
      spacing={{ xs: 0.75, md: 1.25 }}
      sx={{ position: "relative", p: { xs: 1, md: 2 }, minWidth: 0 }}
    >
      <Tooltip title="Tirar da comparação">
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{ position: "absolute", top: 0, right: 0 }}
          aria-label="Tirar da comparação"
        >
          <Close fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          height: { xs: 26, md: 40 },
          pr: 4,
          "& svg": { height: "100%", width: "auto", maxWidth: "100%" },
        }}
      >
        {getPartnerLogo(plan.insuranceCompany)?.component.dark}
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: "primary.main",
          fontSize: { xs: "0.85rem", md: "1rem" },
          lineHeight: 1.25,
        }}
      >
        {plan.insuranceName}
      </Typography>
      <Box>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.1rem", md: "1.6rem" },
            lineHeight: 1.1,
          }}
        >
          {formatBRL(price)}
        </Typography>
        {perDay > 0 && (
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.75rem", md: "0.85rem" },
            }}
          >
            {formatBRL(perDay)}/dia
          </Typography>
        )}
      </Box>
      <Stack
        direction="row"
        sx={{
          flexWrap: "wrap",
          gap: 0.5,
          alignContent: "flex-start",
          minHeight: { md: 56 },
        }}
      >
        {badges.map((badge) => (
          <Tooltip key={badge} title={BADGE_HINTS[badge]}>
            <Chip
              label={badge}
              size="small"
              color={BADGE_COLORS[badge]}
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.65rem", md: "0.75rem" },
                height: "auto",
                py: 0.25,
                "& .MuiChip-label": { whiteSpace: "normal" },
              }}
            />
          </Tooltip>
        ))}
        {isStudentOnly(plan) && (
          <Chip
            label="Exclusivo para estudantes"
            size="small"
            color="warning"
            variant="outlined"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "0.65rem", md: "0.75rem" },
              height: "auto",
              py: 0.25,
              "& .MuiChip-label": { whiteSpace: "normal" },
            }}
          />
        )}
        {isEurope && planSchengen(plan) && (
          <Chip
            icon={<CheckCircle />}
            label="Schengen"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: { xs: "0.65rem", md: "0.75rem" } }}
          />
        )}
      </Stack>
      <Tooltip title="Quantas das coberturas da tabela abaixo este plano inclui. Itens que a seguradora não informou na cotação (—) não contam.">
        <Box>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            <Box component="b" sx={{ color: "text.primary" }}>
              {count.covered} de {count.total}
            </Box>{" "}
            coberturas ⓘ
          </Typography>
          <LinearProgress
            variant="determinate"
            value={count.total ? (count.covered / count.total) * 100 : 0}
            sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
          />
        </Box>
      </Tooltip>
      <Button
        variant="contained"
        onClick={onSelect}
        endIcon={
          <East
            sx={{
              transform: "rotate(-45deg)",
              display: { xs: "none", md: "inline" },
            }}
          />
        }
        sx={{
          px: { xs: 1, md: 2 },
          fontSize: { xs: "0.75rem", md: "0.875rem" },
        }}
      >
        Selecionar
      </Button>
      {plan.coverageList?.length > 0 && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setCoverageOpen(true)}
          sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" }, textAlign: "left" }}
        >
          Cobertura completa
        </Link>
      )}
      <CoverageDialog
        insurance={plan}
        open={coverageOpen}
        onClose={() => setCoverageOpen(false)}
      />
    </Stack>
  );
};

const Compare = () => {
  const navigate = useNavigate();
  const selectInsurance = useSelectInsurance();
  const tripDays = useTripDays();
  const { compareList, toggleCompare } = useContext(CompareContext);
  const { destination, youngPassengers, oldPassengers } =
    useContext(TripFiltersContext);
  const [onlyDifferences, setOnlyDifferences] = useState(false);

  // A faixa fina com nome/preço só aparece quando o cabeçalho completo sai da tela.
  const headerRef = useRef(null);
  const [headerVisible, setHeaderVisible] = useState(true);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setHeaderVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  });

  const isEurope = destination === "Europa";
  const plans = compareList;
  const passengers = youngPassengers + oldPassengers;

  const comparison = useMemo(
    () => buildComparison(plans, isEurope),
    [plans, isEurope],
  );
  const counts = useMemo(
    () => coverageCounts(comparison, plans.length),
    [comparison, plans.length],
  );
  const badges = planBadges(plans, youngPassengers, oldPassengers);
  const prices = plans.map((p) =>
    getTotalPrice(p, youngPassengers, oldPassengers),
  );

  const backToResults = () => navigate("/search-results");

  if (plans.length < 2) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <Typography variant="h2" sx={{ fontSize: "1.6rem", mb: 1.5 }}>
          Escolha pelo menos 2 planos
        </Typography>
        <Typography sx={{ color: "text.secondary", mb: 3 }}>
          Nos resultados da cotação, marque "Comparar" nos planos que quer ver
          lado a lado.
        </Typography>
        <Button
          variant="contained"
          onClick={destination ? backToResults : () => navigate("/")}
        >
          {destination ? "Voltar aos resultados" : "Fazer uma cotação"}
        </Button>
      </Container>
    );
  }

  // Desktop: coluna de nomes + uma coluna por plano.
  // Celular: só as colunas dos planos; o nome da linha fica em cima dos valores.
  const columns = {
    xs: `repeat(${plans.length}, minmax(0, 1fr))`,
    md: `220px repeat(${plans.length}, minmax(0, 1fr))`,
  };

  return (
    <Container sx={{ py: { xs: 3, md: 5 } }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={backToResults}
        sx={{ mb: 2, px: 0 }}
      >
        Voltar aos resultados
      </Button>
      {/* Título + controles numa linha só */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "flex-end" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, mb: 0.5 }}
          >
            Comparando {plans.length} planos
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
            {destination} · {tripDays} {tripDays === 1 ? "dia" : "dias"} ·{" "}
            {passengers} {passengers === 1 ? "pessoa" : "pessoas"} · preços
            totais da viagem
          </Typography>
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ flexWrap: "wrap" }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ color: "success.dark" }}
          >
            <EmojiEvents sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
              Maior valor da linha
            </Typography>
          </Stack>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={onlyDifferences}
                onChange={(e) => setOnlyDifferences(e.target.checked)}
              />
            }
            label="Só o que é diferente"
            sx={{
              m: 0,
              "& .MuiFormControlLabel-label": { fontSize: "0.9rem" },
            }}
          />
        </Stack>
      </Stack>

      {/* Cabeçalho completo dos planos (preço, selos, botão) */}
      <Box
        ref={headerRef}
        sx={{
          display: "grid",
          gridTemplateColumns: columns,
          gap: { xs: 0.5, md: 1 },
          borderBottom: "2px solid",
          borderColor: "primary.main",
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "flex-end",
            p: 2,
          }}
        >
          {/* Critério de cada selo (no celular, aparece ao tocar no selo) */}
          <Stack spacing={1}>
            {Object.entries(BADGE_HINTS).map(([badge, hint]) => (
              <Box key={badge}>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: `${BADGE_COLORS[badge]}.dark`,
                  }}
                >
                  {badge}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                >
                  {hint}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
        {plans.map((plan, i) => (
          <PlanHeader
            key={`${plan.insuranceCompany}-${plan.insuranceId}`}
            plan={plan}
            price={prices[i]}
            perDay={tripDays ? prices[i] / tripDays : null}
            badges={badges[i]}
            count={counts[i]}
            isEurope={isEurope}
            onRemove={() => toggleCompare(plan)}
            onSelect={() => selectInsurance(plan)}
          />
        ))}
      </Box>

      {/* Faixa fina que acompanha a rolagem: nome e preço de cada plano,
          para não perder a referência de qual coluna é qual. */}
      <Box
        aria-hidden={headerVisible}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: "background.default",
          borderBottom: "2px solid",
          borderColor: "primary.main",
          boxShadow: "0 6px 12px -10px rgba(0,0,0,0.3)",
          transform: headerVisible ? "translateY(-110%)" : "translateY(0)",
          transition: "transform 0.25s ease",
        }}
      >
        <Container
          sx={{
            display: "grid",
            gridTemplateColumns: columns,
            gap: { xs: 0.5, md: 1 },
            py: 1,
          }}
        >
          <Box sx={{ display: { xs: "none", md: "block" } }} />
          {plans.map((plan, i) => (
            <Box
              key={`${plan.insuranceCompany}-${plan.insuranceId}`}
              sx={{ px: { xs: 1, md: 2 }, minWidth: 0 }}
            >
              <Typography
                noWrap
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  fontSize: { xs: "0.75rem", md: "0.9rem" },
                }}
              >
                {plan.insuranceName}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "0.85rem", md: "1rem" },
                }}
              >
                {formatBRL(prices[i])}
              </Typography>
            </Box>
          ))}
        </Container>
      </Box>

      {/* Tabela */}
      {comparison.map((section) => {
        const rows = onlyDifferences
          ? section.rows.filter((row) => row.differs)
          : section.rows;
        if (!rows.length) return null;
        return (
          <Box key={section.title} sx={{ mt: 3 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.05rem",
                px: { xs: 1, md: 2 },
                py: 1,
                backgroundColor: "grey.50",
                borderRadius: 1,
              }}
            >
              {section.title}
            </Typography>
            {rows.map((row) => (
              <Box
                key={row.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: columns,
                  gap: { xs: 0.5, md: 1 },
                  borderBottom: "1px solid",
                  borderColor: "grey.100",
                  py: 0.5,
                }}
              >
                <Typography
                  sx={{
                    gridColumn: { xs: "1 / -1", md: "auto" },
                    px: { xs: 1, md: 2 },
                    pt: { xs: 0.75, md: 1.25 },
                    pb: { xs: 0, md: 1.25 },
                    fontSize: { xs: "0.8rem", md: "0.95rem" },
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                >
                  {row.label}
                </Typography>
                {row.cells.map((cell, i) => (
                  <Cell key={i} cell={cell} winner={row.winners[i]} />
                ))}
              </Box>
            ))}
          </Box>
        );
      })}

      <Typography variant="body2" sx={{ color: "text.secondary", mt: 4 }}>
        <strong>—</strong> significa que a seguradora não informou o item na
        cotação. Os valores são limites por pessoa, conforme as condições gerais
        de cada seguradora. Euro e dólar são comparados pelo valor nominal.
        Confira a cobertura completa antes de comprar.
      </Typography>
    </Container>
  );
};

export default Compare;
